/**
 * Email header analysis.
 *
 * Every free "header analyzer" shows the Received chain. The useful part is
 * the layer above that: whether SPF/DKIM/DMARC actually *passed* for this
 * specific message, and whether the passing domains were aligned with the
 * From: header. A message can pass SPF and still fail DMARC because the SPF
 * pass was for the bounce domain, not the visible sender — that is the single
 * most common invisible deliverability bug, and record-presence checkers
 * cannot see it at all.
 *
 * Pure function, no I/O. Lives in @aegis/shared rather than
 * @aegis/integrations so the public tool can import it into the browser
 * bundle: pasted headers are analyzed client-side and nobody's mail ever
 * reaches our servers. The same function runs server-side for the inbox
 * placement test, on what the seed mailbox actually received.
 */

export type AuthResult = 'pass' | 'fail' | 'softfail' | 'neutral' | 'none' | 'temperror' | 'permerror';

export interface ReceivedHop {
  index: number;
  from?: string;
  by?: string;
  with?: string;
  timestamp?: string;
  /** Seconds spent at this hop, relative to the next-older hop. */
  delay_sec: number;
}

export interface HeaderAnalysis {
  hops: ReceivedHop[];
  total_delivery_sec: number;
  from_address?: string;
  from_domain?: string;
  return_path?: string;
  return_path_domain?: string;
  message_id?: string;
  subject?: string;
  date?: string;
  spf: { result: AuthResult; domain?: string } | null;
  dkim: { result: AuthResult; domain?: string; selector?: string } | null;
  dmarc: { result: AuthResult; policy?: string } | null;
  alignment: {
    /** Return-Path domain matches From domain (relaxed: same org domain). */
    spf_aligned: boolean;
    /** DKIM d= matches From domain (relaxed: same org domain). */
    dkim_aligned: boolean;
  };
  /** X-Spam-*, SpamAssassin and similar scoring headers, verbatim. */
  spam_headers: Record<string, string>;
  list_unsubscribe?: string;
  has_list_unsubscribe_post: boolean;
  warnings: string[];
}

/**
 * Unfold and split a raw header block into ordered [name, value] pairs.
 *
 * RFC 5322 folding means a header value continues onto any following line that
 * starts with whitespace. Splitting on newlines alone corrupts long DKIM and
 * Authentication-Results headers, which is exactly where the interesting data
 * lives.
 */
export function parseHeaderLines(raw: string): [string, string][] {
  // Stop at the first blank line — everything after is the body.
  const headerBlock = raw.replace(/\r\n/g, '\n').split(/\n\n/)[0] ?? '';
  const out: [string, string][] = [];
  let current: [string, string] | null = null;

  for (const line of headerBlock.split('\n')) {
    if (/^[ \t]/.test(line) && current) {
      current[1] += ` ${line.trim()}`;
      continue;
    }
    const match = line.match(/^([!-9;-~]+):[ \t]*(.*)$/);
    if (!match) continue;
    if (current) out.push(current);
    current = [match[1].toLowerCase(), match[2]];
  }
  if (current) out.push(current);
  return out;
}

function firstValue(headers: [string, string][], name: string): string | undefined {
  return headers.find(([k]) => k === name)?.[1];
}

function allValues(headers: [string, string][], name: string): string[] {
  return headers.filter(([k]) => k === name).map(([, v]) => v);
}

/** Pull the addr-spec out of a From:/Return-Path: value. */
export function extractAddress(value?: string): string | undefined {
  if (!value) return undefined;
  const angled = value.match(/<([^>]+)>/);
  const candidate = (angled ? angled[1] : value).trim();
  return candidate.includes('@') ? candidate.replace(/^"+|"+$/g, '') : undefined;
}

function domainOf(address?: string): string | undefined {
  return address?.split('@').pop()?.toLowerCase().replace(/[>;,\s]+$/, '');
}

/**
 * Organizational-domain comparison (relaxed DMARC alignment).
 *
 * A true implementation needs the Public Suffix List; without it, comparing
 * the last two labels handles the overwhelmingly common cases
 * (mail.example.com vs example.com) and is honest about what it does. Domains
 * under multi-label suffixes such as .co.uk will read as aligned when they
 * share the registrable part, which is the safe direction to err for a
 * diagnostic — it never invents a failure that isn't there.
 */
export function sameOrganizationalDomain(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const tail = (d: string) => d.split('.').slice(-2).join('.');
  return tail(a) === tail(b);
}

function normalizeResult(value?: string): AuthResult {
  const v = value?.toLowerCase().trim();
  switch (v) {
    case 'pass':
    case 'fail':
    case 'softfail':
    case 'neutral':
    case 'none':
    case 'temperror':
    case 'permerror':
      return v;
    case 'hardfail':
      return 'fail';
    default:
      return 'none';
  }
}

/**
 * Parse Authentication-Results headers.
 *
 * Multiple such headers can be present (each hop may add one). We take the
 * first result we find for each mechanism, scanning in header order, which is
 * the receiving MTA's own verdict — the one that actually decided placement.
 */
function parseAuthResults(headers: [string, string][]) {
  const lines = [
    ...allValues(headers, 'authentication-results'),
    ...allValues(headers, 'arc-authentication-results'),
  ];

  let spf: HeaderAnalysis['spf'] = null;
  let dkim: HeaderAnalysis['dkim'] = null;
  let dmarc: HeaderAnalysis['dmarc'] = null;

  for (const line of lines) {
    if (!spf) {
      const m = line.match(/\bspf=(\w+)/i);
      if (m) {
        const domain =
          line.match(/smtp\.mailfrom=([^\s;()]+)/i)?.[1] ||
          line.match(/envelope-from=([^\s;()]+)/i)?.[1];
        spf = { result: normalizeResult(m[1]), domain: domainOf(domain) ?? domain?.toLowerCase() };
      }
    }
    if (!dkim) {
      const m = line.match(/\bdkim=(\w+)/i);
      if (m) {
        dkim = {
          result: normalizeResult(m[1]),
          domain: line.match(/header\.(?:d|i)=([^\s;()]+)/i)?.[1]?.toLowerCase().replace(/^@/, ''),
          selector: line.match(/header\.s=([^\s;()]+)/i)?.[1],
        };
      }
    }
    if (!dmarc) {
      const m = line.match(/\bdmarc=(\w+)/i);
      if (m) {
        dmarc = {
          result: normalizeResult(m[1]),
          policy: line.match(/\bp=(\w+)/i)?.[1]?.toLowerCase(),
        };
      }
    }
  }

  // Fall back to the older Received-SPF header when no Authentication-Results
  // is present — still common on smaller receiving servers.
  if (!spf) {
    const received = firstValue(headers, 'received-spf');
    if (received) {
      spf = {
        result: normalizeResult(received.match(/^\s*(\w+)/)?.[1]),
        domain: domainOf(
          received.match(/envelope-from=([^\s;()]+)/i)?.[1] ??
            received.match(/smtp\.mailfrom=([^\s;()]+)/i)?.[1],
        ),
      };
    }
  }

  // If there is no Authentication-Results DKIM verdict but the message carries
  // a DKIM-Signature, report the signing domain with an unknown result rather
  // than claiming no DKIM at all.
  if (!dkim) {
    const sig = firstValue(headers, 'dkim-signature');
    if (sig) {
      dkim = {
        result: 'none',
        domain: sig.match(/\bd=([^;\s]+)/i)?.[1]?.toLowerCase(),
        selector: sig.match(/\bs=([^;\s]+)/i)?.[1],
      };
    }
  }

  return { spf, dkim, dmarc };
}

function parseHops(headers: [string, string][]): { hops: ReceivedHop[]; total: number } {
  // Received headers are prepended, so header order is newest-first. Reverse
  // to walk the message forward in time the way a human reads a trace.
  const received = allValues(headers, 'received').reverse();

  const parsed = received.map((value, index) => {
    // The timestamp follows the last semicolon in the header value.
    const tsPart = value.split(';').pop()?.trim();
    const ts = tsPart ? Date.parse(tsPart) : NaN;
    return {
      index,
      from: value.match(/\bfrom\s+([^\s;]+)/i)?.[1],
      by: value.match(/\bby\s+([^\s;]+)/i)?.[1],
      with: value.match(/\bwith\s+([A-Za-z0-9]+)/i)?.[1],
      timestamp: Number.isNaN(ts) ? undefined : new Date(ts).toISOString(),
      epoch: Number.isNaN(ts) ? undefined : ts,
      delay_sec: 0,
    };
  });

  for (let i = 1; i < parsed.length; i++) {
    const prev = parsed[i - 1].epoch;
    const curr = parsed[i].epoch;
    // Clock skew between servers routinely produces negative deltas; clamp so
    // a hop never reports travelling backwards in time.
    if (prev !== undefined && curr !== undefined) {
      parsed[i].delay_sec = Math.max(0, Math.round((curr - prev) / 1000));
    }
  }

  const stamps = parsed.map((p) => p.epoch).filter((e): e is number => e !== undefined);
  const total = stamps.length >= 2 ? Math.max(0, Math.round((stamps[stamps.length - 1] - stamps[0]) / 1000)) : 0;

  return {
    hops: parsed.map(({ epoch: _epoch, ...hop }) => hop),
    total,
  };
}

export function analyzeEmailHeaders(raw: string): HeaderAnalysis {
  const headers = parseHeaderLines(raw);
  const { hops, total } = parseHops(headers);
  const { spf, dkim, dmarc } = parseAuthResults(headers);

  const fromAddress = extractAddress(firstValue(headers, 'from'));
  const fromDomain = domainOf(fromAddress);
  const returnPath = extractAddress(firstValue(headers, 'return-path'));
  const returnPathDomain = domainOf(returnPath);

  const spfAligned = sameOrganizationalDomain(fromDomain, spf?.domain ?? returnPathDomain);
  const dkimAligned = sameOrganizationalDomain(fromDomain, dkim?.domain);

  const spamHeaders: Record<string, string> = {};
  for (const [name, value] of headers) {
    if (/^(x-spam|x-spamd|x-rspamd|x-barracuda|x-microsoft-antispam|x-forefront-antispam)/.test(name)) {
      spamHeaders[name] = value;
    }
  }

  const warnings: string[] = [];
  if (!headers.length) warnings.push('No headers could be parsed — paste the full raw headers.');
  if (!fromAddress) warnings.push('No From: address found.');
  if (spf && spf.result !== 'pass') warnings.push(`SPF did not pass (${spf.result}).`);
  if (dkim && dkim.result !== 'pass') {
    warnings.push(
      dkim.result === 'none'
        ? 'No DKIM verification result — the receiver did not report one.'
        : `DKIM did not pass (${dkim.result}).`,
    );
  }
  if (dmarc && dmarc.result !== 'pass') warnings.push(`DMARC did not pass (${dmarc.result}).`);
  if (!dmarc) warnings.push('No DMARC result reported by the receiving server.');
  if (spf?.result === 'pass' && !spfAligned) {
    warnings.push(
      'SPF passed but is not aligned with the From: domain — it authenticated the bounce domain, so it does not satisfy DMARC.',
    );
  }
  if (dkim?.result === 'pass' && !dkimAligned) {
    warnings.push('DKIM passed but the signing domain is not aligned with the From: domain.');
  }
  if (total > 300) {
    warnings.push(`Delivery took ${total}s — long delays often indicate greylisting or queue pressure.`);
  }
  if (!firstValue(headers, 'list-unsubscribe')) {
    warnings.push('No List-Unsubscribe header — required by Gmail and Yahoo for bulk senders.');
  }

  return {
    hops,
    total_delivery_sec: total,
    from_address: fromAddress,
    from_domain: fromDomain,
    return_path: returnPath,
    return_path_domain: returnPathDomain,
    message_id: firstValue(headers, 'message-id'),
    subject: firstValue(headers, 'subject'),
    date: firstValue(headers, 'date'),
    spf,
    dkim,
    dmarc,
    alignment: { spf_aligned: spfAligned, dkim_aligned: dkimAligned },
    spam_headers: spamHeaders,
    list_unsubscribe: firstValue(headers, 'list-unsubscribe'),
    has_list_unsubscribe_post: Boolean(firstValue(headers, 'list-unsubscribe-post')),
    warnings,
  };
}
